import { useEffect, useRef, type RefObject } from "react";
import {
  FRAME_CAPTURE_INTERVAL_MS,
  MAX_CAPTURE_WIDTH,
  MAX_CAPTURE_HEIGHT,
} from "@core/features/video/video.constants";

/**
 * Periodically captures video frames to a canvas element as a fallback
 * for simulcast rebinding scenarios where Chrome clears the decoded frame
 * buffer before React can react.
 *
 * While `isCapturing` is true, captures a frame every FRAME_CAPTURE_INTERVAL_MS.
 * When `isCapturing` becomes false, the interval stops but the last drawn frame
 * is preserved on the canvas for display as a frozen-frame overlay.
 */
export function useVideoFrameCapture(
  videoRef: RefObject<HTMLVideoElement | null>,
  canvasRef: RefObject<HTMLCanvasElement | null>,
  isCapturing: boolean,
) {
  const lastWidthRef = useRef(0);
  const lastHeightRef = useRef(0);

  useEffect(() => {
    if (!isCapturing) return;

    const id = setInterval(() => {
      const el = videoRef.current;
      const canvas = canvasRef.current;
      if (!el || !canvas) return;

      // Guard: video must have decoded data and non-zero dimensions.
      // Chrome sets videoWidth to 0 before clearing the buffer during
      // simulcast rebinding — this check prevents capturing a blank frame.
      if (el.readyState < 2 || el.videoWidth <= 0 || el.videoHeight <= 0) {
        return;
      }

      const scale = Math.min(
        MAX_CAPTURE_WIDTH / el.videoWidth,
        MAX_CAPTURE_HEIGHT / el.videoHeight,
        1,
      );
      const w = Math.round(el.videoWidth * scale);
      const h = Math.round(el.videoHeight * scale);

      // Only update canvas dimensions when they change — setting width/height
      // clears the canvas content, which would destroy the last good frame.
      if (w !== lastWidthRef.current || h !== lastHeightRef.current) {
        canvas.width = w;
        canvas.height = h;
        lastWidthRef.current = w;
        lastHeightRef.current = h;
      }

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(el, 0, 0, w, h);
      }
    }, FRAME_CAPTURE_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isCapturing, videoRef, canvasRef]);
}
